/**
 * Browser adapters: session storage (localStorage), connectivity, GPS, single-tab lock,
 * persistent storage, service worker registration, install prompt.
 */
import type { SecureKV } from "@core/session";
import type { Fix } from "@core/location";

/* ---------------- key/value for the session (token, user, device id) ---------------- */

export const localKV: SecureKV = {
  async getItem(k) {
    try {
      return localStorage.getItem(k);
    } catch {
      return null;
    }
  },
  async setItem(k, v) {
    localStorage.setItem(k, v);
  },
  async deleteItem(k) {
    try {
      localStorage.removeItem(k);
    } catch {
      /* ignore */
    }
  },
};

/** Small UI preferences (language, dismissed hints, link). Never throws. */
export const prefs = {
  get(k: string): string | null {
    try {
      return localStorage.getItem(`npac.pref.${k}`);
    } catch {
      return null;
    }
  },
  set(k: string, v: string | null) {
    try {
      if (v == null) localStorage.removeItem(`npac.pref.${k}`);
      else localStorage.setItem(`npac.pref.${k}`, v);
    } catch {
      /* ignore */
    }
  },
};

export function makeId(): string {
  // randomUUID needs a secure context (https / localhost); fall back to getRandomValues
  if (typeof crypto.randomUUID === "function") return crypto.randomUUID();
  const b = crypto.getRandomValues(new Uint8Array(16));
  return Array.from(b, (x: number) => x.toString(16).padStart(2, "0")).join("");
}

/* ---------------- SOS photo ---------------- */

/**
 * Shrink a camera photo for the SOS: longest side at most `maxSide` px, JPEG, stepping the quality
 * down until it fits `maxBytes`. Phone photos are 3-8 MB; this makes them ~100-300 KB, which also
 * gets through a weak polling-booth network.
 */
export async function compressPhoto(file: Blob, maxSide = 1280, maxBytes = 900_000): Promise<{ type: "image/jpeg"; data: string; bytes: number }> {
  const img = await loadImage(file);
  const scale = Math.min(1, maxSide / Math.max(img.width, img.height));
  const w = Math.max(1, Math.round(img.width * scale));
  const h = Math.max(1, Math.round(img.height * scale));
  const canvas = document.createElement("canvas");
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("canvas unavailable");
  ctx.fillStyle = "#fff";
  ctx.fillRect(0, 0, w, h);
  ctx.drawImage(img.source, 0, 0, w, h);
  img.close();
  for (const q of [0.72, 0.6, 0.48, 0.36]) {
    const blob = await new Promise<Blob | null>((res) => canvas.toBlob(res, "image/jpeg", q));
    if (blob && blob.size <= maxBytes) return { type: "image/jpeg", data: await blobBase64(blob), bytes: blob.size };
  }
  throw new Error("photo too large");
}

async function loadImage(file: Blob): Promise<{ source: CanvasImageSource; width: number; height: number; close: () => void }> {
  if (typeof createImageBitmap === "function") {
    try {
      // honours the camera's EXIF rotation where supported
      const bmp = await createImageBitmap(file, { imageOrientation: "from-image" } as ImageBitmapOptions);
      return { source: bmp, width: bmp.width, height: bmp.height, close: () => bmp.close() };
    } catch {
      /* fall back to <img> */
    }
  }
  const url = URL.createObjectURL(file);
  try {
    const el = new Image();
    await new Promise<void>((res, rej) => {
      el.onload = () => res();
      el.onerror = () => rej(new Error("not an image"));
      el.src = url;
    });
    return { source: el, width: el.naturalWidth, height: el.naturalHeight, close: () => undefined };
  } finally {
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  }
}

function blobBase64(b: Blob): Promise<string> {
  return new Promise((res, rej) => {
    const r = new FileReader();
    r.onload = () => res(String(r.result).replace(/^data:[^,]*,/, ""));
    r.onerror = () => rej(r.error ?? new Error("read failed"));
    r.readAsDataURL(b);
  });
}

/* ---------------- connectivity ---------------- */

/** navigator.onLine is only a hint (it is true on Wi-Fi without internet); the outbox copes either way. */
export function watchOnline(cb: (online: boolean) => void): () => void {
  const on = () => cb(true);
  const off = () => cb(false);
  window.addEventListener("online", on);
  window.addEventListener("offline", off);
  cb(navigator.onLine);
  return () => {
    window.removeEventListener("online", on);
    window.removeEventListener("offline", off);
  };
}

/* ---------------- GPS ---------------- */

export type GeoError = "insecure" | "unavailable" | "denied" | "timeout";

export interface GeoWatch {
  stop(): void;
}

/** watchPosition with high accuracy; every reading is passed on (the caller keeps the best). */
export function watchGps(onFix: (f: Fix) => void, onError: (e: GeoError) => void): GeoWatch {
  if (typeof window !== "undefined" && !window.isSecureContext) {
    onError("insecure");
    return { stop() {} };
  }
  if (!("geolocation" in navigator)) {
    onError("unavailable");
    return { stop() {} };
  }
  const id = navigator.geolocation.watchPosition(
    (p) => onFix({ lat: p.coords.latitude, lng: p.coords.longitude, accuracyM: Number.isFinite(p.coords.accuracy) ? p.coords.accuracy : null, timestamp: p.timestamp || Date.now() }),
    (e) => onError(e.code === e.PERMISSION_DENIED ? "denied" : e.code === e.TIMEOUT ? "timeout" : "unavailable"),
    { enableHighAccuracy: true, maximumAge: 0, timeout: 30_000 }
  );
  return { stop: () => navigator.geolocation.clearWatch(id) };
}

/* ---------------- one tab at a time ---------------- */

/**
 * The database lives in memory and is written to IndexedDB as a whole; two tabs would overwrite
 * each other's changes. Only the first tab gets the lock; others show a message.
 */
export function acquireSingleTabLock(): Promise<boolean> {
  const locks = (navigator as Navigator & { locks?: LockManager }).locks;
  if (!locks) return Promise.resolve(true);
  return new Promise((resolve) => {
    void locks.request("npac-field-app", { ifAvailable: true }, (lock) => {
      if (!lock) {
        resolve(false);
        return undefined;
      }
      resolve(true);
      return new Promise<void>(() => undefined); // hold until the page closes
    });
  });
}

/** Ask the browser not to evict our storage under pressure (granted automatically for installed PWAs). */
export async function requestPersistentStorage(): Promise<boolean> {
  try {
    return (await navigator.storage?.persist?.()) ?? false;
  } catch {
    return false;
  }
}

/* ---------------- service worker ---------------- */

export interface SwHandle {
  /** a new version is installed and waiting; returns an unsubscribe function */
  onUpdate(cb: () => void): () => void;
  applyUpdate(): void;
  checkForUpdate(): void;
}

export function registerServiceWorker(): SwHandle {
  const listeners = new Set<() => void>();
  let reg: ServiceWorkerRegistration | null = null;
  let waiting = false;
  const notify = () => {
    waiting = true;
    listeners.forEach((l) => l());
  };
  if ("serviceWorker" in navigator && import.meta.env.PROD) {
    navigator.serviceWorker
      .register("./sw.js", { scope: "./" })
      .then((r) => {
        reg = r;
        if (r.waiting && navigator.serviceWorker.controller) notify();
        r.addEventListener("updatefound", () => {
          const w = r.installing;
          w?.addEventListener("statechange", () => {
            // "installed" with an existing controller = an update (not the first install)
            if (w.state === "installed" && navigator.serviceWorker.controller) notify();
          });
        });
      })
      .catch(() => undefined);
    let reloading = false;
    navigator.serviceWorker.addEventListener("controllerchange", () => {
      if (reloading || !sessionStorage.getItem("npac.sw.update")) return;
      reloading = true;
      sessionStorage.removeItem("npac.sw.update");
      location.reload();
    });
  }
  return {
    onUpdate: (cb) => {
      listeners.add(cb);
      if (waiting) cb();
      return () => {
        listeners.delete(cb);
      };
    },
    applyUpdate: () => {
      if (!reg?.waiting) return;
      sessionStorage.setItem("npac.sw.update", "1");
      reg.waiting.postMessage({ type: "SKIP_WAITING" });
    },
    checkForUpdate: () => void reg?.update().catch(() => undefined),
  };
}

/* ---------------- install ---------------- */

interface BeforeInstallPromptEvent extends Event {
  prompt(): Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
}

let deferredPrompt: BeforeInstallPromptEvent | null = null;
const installListeners = new Set<() => void>();
if (typeof window !== "undefined") {
  window.addEventListener("beforeinstallprompt", (e) => {
    e.preventDefault();
    deferredPrompt = e as BeforeInstallPromptEvent;
    installListeners.forEach((l) => l());
  });
  window.addEventListener("appinstalled", () => {
    deferredPrompt = null;
    prefs.set("installHintDone", "1");
    installListeners.forEach((l) => l());
  });
}

export const install = {
  canPrompt: () => !!deferredPrompt,
  async prompt() {
    const p = deferredPrompt;
    if (!p) return false;
    deferredPrompt = null;
    await p.prompt();
    return (await p.userChoice).outcome === "accepted";
  },
  subscribe(cb: () => void) {
    installListeners.add(cb);
    return () => installListeners.delete(cb);
  },
  isStandalone(): boolean {
    return window.matchMedia?.("(display-mode: standalone)").matches || (navigator as Navigator & { standalone?: boolean }).standalone === true;
  },
};

export function isIOS(): boolean {
  return /iPad|iPhone|iPod/.test(navigator.userAgent) || (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);
}
